import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { CaseSummary, Paginated } from '@foundation/shared';
import {
  CASE_PRIORITIES,
  CASE_PRIORITY_LABEL_TH,
  CASE_STATUSES,
  CASE_STATUS_LABEL_TH,
} from '@foundation/shared';
import { api, buildQuery } from '../lib/api';
import { formatDate, formatSatang } from '../lib/format';
import { Empty, ErrorAlert, Loading } from '../components/ui';
import { useAuth } from '../auth';

const STATUS_TONE: Record<string, string> = {
  draft: '',
  submitted: 'amber',
  under_review: 'amber',
  approved: 'green',
  rejected: 'red',
  in_progress: 'blue',
  completed: 'green',
  cancelled: '',
};

const PRIORITY_TONE: Record<string, string> = {
  urgent: 'red',
  high: 'amber',
  normal: '',
  low: '',
};

export default function CaseList() {
  const { allowed } = useAuth();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState<CaseSummary[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [q, setQ] = useState('');

  const status = params.get('status') ?? '';
  const priority = params.get('priority') ?? '';
  const mine = params.get('awaitingMyApproval') === 'true';
  const overdue = params.get('overdue') === 'true';

  const load = useCallback(
    async (append = false, from?: string) => {
      setError(null);
      try {
        const res = await api<Paginated<CaseSummary>>(
          `/cases${buildQuery({
            status,
            priority,
            q,
            awaitingMyApproval: mine ? 'true' : undefined,
            overdue: overdue ? 'true' : undefined,
            sort: overdue ? 'due_date' : 'recent',
            limit: 50,
            cursor: from,
          })}`,
        );
        setRows((prev) => (append && prev ? [...prev, ...res.data] : res.data));
        setCursor(res.nextCursor);
      } catch (e) {
        setError(e);
        if (!append) setRows([]);
      }
    },
    [status, priority, q, mine, overdue],
  );

  useEffect(() => {
    const t = setTimeout(() => void load(), q ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  const pendingApproval = (rows ?? []).filter((r) =>
    ['submitted', 'under_review'].includes(r.status),
  );

  return (
    <>
      <div className="page-head">
        <div>
          <h1>เคสความช่วยเหลือ</h1>
          <p>ตั้งแต่ยื่นคำขอ อนุมัติตามวงเงิน ไปจนถึงจ่ายจริงและปิดเคส</p>
        </div>
        {allowed('case:write') && (
          <Link className="btn" to="/cases/new">
            + เปิดเคสใหม่
          </Link>
        )}
      </div>

      <ErrorAlert error={error} />

      {mine && pendingApproval.length > 0 && (
        <div className="alert info">
          มี {pendingApproval.length} เคสที่รอให้คุณพิจารณา
        </div>
      )}

      <div className="card">
        <div className="toolbar">
          <div className="field grow">
            <label htmlFor="cq">ค้นหา</label>
            <input
              id="cq"
              type="search"
              placeholder="เลขที่เคส ชื่อเรื่อง หรือรหัสผู้รับความช่วยเหลือ"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="cstatus">สถานะ</label>
            <select id="cstatus" value={status} onChange={(e) => setParam('status', e.target.value)}>
              <option value="">ทั้งหมด</option>
              {CASE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {CASE_STATUS_LABEL_TH[s]}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="cprio">ความเร่งด่วน</label>
            <select
              id="cprio"
              value={priority}
              onChange={(e) => setParam('priority', e.target.value)}
            >
              <option value="">ทั้งหมด</option>
              {CASE_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {CASE_PRIORITY_LABEL_TH[p]}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="btn-row">
          <label className="checkbox">
            <input
              type="checkbox"
              checked={mine}
              onChange={(e) => setParam('awaitingMyApproval', e.target.checked ? 'true' : '')}
            />
            เฉพาะที่รออนุมัติโดยฉัน
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={overdue}
              onChange={(e) => setParam('overdue', e.target.checked ? 'true' : '')}
            />
            เฉพาะที่เลยกำหนด
          </label>
        </div>

        {rows === null ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>
            {mine ? 'ไม่มีเคสที่รอให้คุณอนุมัติ 🎉' : 'ยังไม่มีเคสตามเงื่อนไขนี้'}
          </Empty>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>เลขที่เคส</th>
                    <th className="wrap">ผู้รับความช่วยเหลือ</th>
                    <th className="wrap">เรื่อง</th>
                    <th>ประเภท</th>
                    <th>ขอ</th>
                    <th>อนุมัติ</th>
                    <th>จ่ายแล้ว</th>
                    <th>กำหนดเสร็จ</th>
                    <th>สถานะ</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td className="mono small">
                        <Link className="row-link" to={`/cases/${r.id}`}>
                          {r.caseCode}
                        </Link>
                        {(r.priority === 'urgent' || r.priority === 'high') && (
                          <div className={`badge ${PRIORITY_TONE[r.priority]}`}>
                            {CASE_PRIORITY_LABEL_TH[r.priority]}
                          </div>
                        )}
                      </td>
                      <td className="wrap">
                        {r.beneficiaryName}
                        <div className="muted small">
                          {r.benCode}
                          {r.areaName ? ` · ${r.areaName}` : ''}
                        </div>
                      </td>
                      <td className="wrap">{r.title}</td>
                      <td className="small">{r.aidTypeName}</td>
                      <td className="small">{formatSatang(r.requestedAmount)}</td>
                      <td style={{ fontWeight: 600 }}>{formatSatang(r.approvedAmount)}</td>
                      <td className="small">{formatSatang(r.disbursedAmount)}</td>
                      <td className="small muted">
                        {formatDate(r.dueDate)}
                        {r.isOverdue && <div className="badge red">เลยกำหนด</div>}
                      </td>
                      <td>
                        <span className={`badge ${STATUS_TONE[r.status]}`}>
                          {CASE_STATUS_LABEL_TH[r.status]}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {cursor && (
              <div className="btn-row">
                <button className="btn ghost" onClick={() => void load(true, cursor)}>
                  โหลดเพิ่ม
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
